import { Box, Tooltip } from "@mui/material";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import * as React from "react";

interface DayWithTripIndicatorProps extends PickersDayProps {
  tripCountsByDate?: Map<string, number>;
}

/**
 * Custom day component for MUI date pickers that displays a dot indicator
 * below dates with scheduled trips. Dot size and opacity scale with trip count.
 *
 * Inspired by Resy's booking calendar design - subtle, clean, professional.
 */
export const DayWithTripIndicator: React.FC<DayWithTripIndicatorProps> = (
  props
) => {
  const { day, tripCountsByDate, ...other } = props;

  // Get trip count for this date
  const dateKey = day.toISOString().split("T")[0]; // YYYY-MM-DD format
  const tripCount = tripCountsByDate?.get(dateKey) || 0;

  // Calculate dot styling based on trip count
  const getDotStyle = (count: number) => {
    if (count === 0) {
      return null; // No dot
    } else if (count <= 2) {
      return { size: 4, opacity: 0.6 }; // Small, subtle
    } else if (count <= 5) {
      return { size: 6, opacity: 0.8 }; // Medium
    } else {
      return { size: 8, opacity: 1.0 }; // Large, prominent
    }
  };

  const dotStyle = getDotStyle(tripCount);

  return (
    <Tooltip
      title={tripCount > 0 ? `${tripCount} trip${tripCount > 1 ? "s" : ""}` : ""}
      arrow
      placement="top"
      disableInteractive
    >
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <PickersDay day={day} {...other} />
        {dotStyle && (
          <Box
            sx={{
              position: "absolute",
              bottom: 2,
              width: dotStyle.size,
              height: dotStyle.size,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              opacity: dotStyle.opacity,
              transition: "all 0.2s ease-in-out",
              // Add subtle shadow for depth on larger dots
              ...(dotStyle.size >= 6 && {
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              }),
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};
